import { SEO } from "@/components/SEO";
import { CalendlyEmbed } from "@/components/CalendlyEmbed";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { CheckCircle, XCircle, Phone, MessageSquare, Clock, Shield, ArrowRight, Zap } from "lucide-react";

const LawnCareQuoteForm = () => {
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const faqs = [
    {
      question: "How many fields should a lawn care quote form have?",
      answer: "4 fields maximum: Name, Phone, City/Zip, and Service Needed. Every additional field reduces conversions by 5-10%. Keep it simple—you can gather more details on the follow-up call."
    },
    {
      question: "Should I require an email address on my form?",
      answer: "Phone is more valuable for lawn care. Most homeowners prefer a quick call over email chains. If you want email for follow-up marketing, make it optional—never required."
    },
    {
      question: "Where should I place my quote form on the website?",
      answer: "Above the fold on your homepage, at the bottom of every service page, and consider a sticky mobile button. The easier it is to find, the more leads you'll capture."
    },
    {
      question: "How do I stop spam submissions?",
      answer: "Use a honeypot field (hidden field bots fill out), require a valid phone format, and add simple validation. Avoid CAPTCHAs—they frustrate real customers and hurt conversions."
    },
    {
      question: "How fast should I respond to form submissions?",
      answer: "Within 5 minutes is ideal. Studies show leads contacted within 5 minutes are 21x more likely to convert. Set up auto-text confirmations to buy time if you're busy."
    },
    {
      question: "Should I use a multi-step form or single form?",
      answer: "For lawn care, stick with a single short form. Multi-step forms work for complex services but add unnecessary friction for simple quote requests like mowing or fertilization."
    },
    {
      question: "Do I need a separate form for each service?",
      answer: "No. One form with a service dropdown works best. It keeps your site simpler and lets you route leads appropriately based on their selection."
    },
    {
      question: "What confirmation should visitors see after submitting?",
      answer: "Show a clear thank-you message with expected response time (e.g., 'We'll call within 2 hours'). Send an instant auto-text or email confirmation so they know the form worked."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-quote-form/#faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.leafrushmarketing.com/lawn-care-quote-form/#webpage",
    "url": "https://www.leafrushmarketing.com/lawn-care-quote-form/",
    "name": "Lawn Care Quote Form Best Practices | Leaf Rush Marketing",
    "description": "Learn how to build a high-converting lawn care quote form. 4 fields, smart placement, spam control, and speed-to-lead tips that turn visitors into customers.",
    "isPartOf": {
      "@id": "https://www.leafrushmarketing.com/#website"
    },
    "about": {
      "@id": "https://www.leafrushmarketing.com/#organization"
    },
    "dateModified": new Date().toISOString().split('T')[0]
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://www.leafrushmarketing.com/#organization",
    "name": "Leaf Rush Marketing",
    "url": "https://www.leafrushmarketing.com",
    "logo": "https://www.leafrushmarketing.com/og-image.jpg",
    "sameAs": []
  };

  return (
    <>
      <SEO
        title="Lawn Care Quote Form Best Practices | Leaf Rush Marketing"
        description="Learn how to build a high-converting lawn care quote form. 4 fields, smart placement, spam control, and speed-to-lead tips that turn visitors into customers."
        canonical="https://www.leafrushmarketing.com/lawn-care-quote-form/"
        ogUrl="https://www.leafrushmarketing.com/lawn-care-quote-form/"
        ogImage="https://www.leafrushmarketing.com/og-image.jpg"
        keywords="lawn care quote form, lawn care estimate form, lawn care website form, lawn care lead form, short form conversions, lawn care contact form"
      />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />

      <div className="pt-24">
        {/* Hero Section */}
        <section className="py-16 md:py-20 bg-gradient-to-b from-primary/5 to-background">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6">
                Lawn Care Quote Form Best Practices
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-4">
                Your quote form is the gateway to every lead. Too many fields? They leave. 
                Wrong placement? They never find it. No follow-up system? They forget you.
              </p>
              <p className="text-lg text-muted-foreground">
                Here's how to build a form that actually converts—and what to do the moment someone submits.
              </p>
            </div>
          </div>
        </section>

        {/* Perfect 4-Field Form */}
        <section className="py-16 bg-background">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                The Perfect 4-Field Form
              </h2>
              <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
                Every field you add costs you leads. These 4 fields give you everything needed to call back—nothing more.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">1</div>
                    <h3 className="font-semibold text-lg">Name</h3>
                  </div>
                  <p className="text-muted-foreground">
                    First name only is fine. You need something to call them—"Hi John" beats "Hi there."
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">2</div>
                    <h3 className="font-semibold text-lg">Phone Number</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Your #1 priority. Lawn care customers expect a call, not an email thread. Make this required.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">3</div>
                    <h3 className="font-semibold text-lg">City or Zip Code</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Instantly tells you if they're in your service area. Zip is faster to type than full address.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold">4</div>
                    <h3 className="font-semibold text-lg">Service Needed</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Simple dropdown: Mowing, Fertilization, Aeration, Cleanup, Other. Helps you prepare for the call.
                  </p>
                </div>
              </div>

              <div className="mt-10 p-6 bg-accent/30 rounded-xl border border-accent">
                <p className="text-center font-medium">
                  <strong>Why not more fields?</strong> Each additional field reduces conversions by 5-10%. 
                  You'll get the address, lawn size, and details on the phone—where you can also build rapport.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Best Placements */}
        <section className="py-16 bg-muted/30">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                Best Form Placements
              </h2>
              <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
                The right form in the wrong spot is invisible. Put it where people actually look.
              </p>

              <div className="space-y-6">
                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                    <Zap className="h-5 w-5 text-primary" />
                    Hero Section (Above the Fold)
                  </h3>
                  <p className="text-muted-foreground">
                    Sidebar form or inline form right on your homepage hero. Visitors who know what they want can convert immediately.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                    <ArrowRight className="h-5 w-5 text-primary" />
                    Bottom of Every Service Page
                  </h3>
                  <p className="text-muted-foreground">
                    After they read about <Link to="/service-pages-that-rank/" className="text-primary hover:underline">your mowing or fertilization service</Link>, 
                    make it easy to request a quote without scrolling back up.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-primary" />
                    Footer (Site-Wide)
                  </h3>
                  <p className="text-muted-foreground">
                    A compact form in the footer catches visitors who scroll all the way down. It's your safety net.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                    <Phone className="h-5 w-5 text-primary" />
                    Mobile Sticky Call Button
                  </h3>
                  <p className="text-muted-foreground">
                    On mobile, a sticky "Call Now" or "Get Quote" button at the bottom of the screen 
                    outperforms forms. Tapping to call is faster than typing on a phone.
                  </p>
                </div>
              </div>

              <p className="mt-8 text-center text-muted-foreground">
                Need help with <Link to="/service-area-pages-local-seo/" className="text-primary hover:underline">service area pages</Link>? 
                Each one should have a form too.
              </p>
            </div>
          </div>
        </section>

        {/* Spam Control */}
        <section className="py-16 bg-background">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                Spam Control (Without Annoying Real Customers)
              </h2>
              <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
                Bots will find your form. Here's how to stop them without CAPTCHA puzzles that frustrate real leads.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <Shield className="h-6 w-6 text-primary" />
                    <h3 className="font-semibold text-lg">Honeypot Field</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Add a hidden field bots will fill out but humans won't see. If it has data, reject the submission.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <Phone className="h-6 w-6 text-primary" />
                    <h3 className="font-semibold text-lg">Phone Format Validation</h3>
                  </div>
                  <p className="text-muted-foreground">
                    Require a valid 10-digit phone number. Bots often submit garbage like "1234567890" or "aaaaaaaaaa."
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="h-6 w-6 text-primary" />
                    <h3 className="font-semibold text-lg">Submission Timing</h3>
                  </div>
                  <p className="text-muted-foreground">
                    If a form is submitted in under 3 seconds, it's probably a bot. Real humans take time to type.
                  </p>
                </div>

                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <XCircle className="h-6 w-6 text-destructive" />
                    <h3 className="font-semibold text-lg">Avoid CAPTCHAs</h3>
                  </div>
                  <p className="text-muted-foreground">
                    "Click all the traffic lights" tests frustrate real customers. They hurt conversions more than spam does.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What Happens After Submit */}
        <section className="py-16 bg-muted/30">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                What Happens After They Submit
              </h2>
              <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
                The form is just step one. Speed to lead determines if they become a customer—or call your competitor.
              </p>

              <div className="space-y-6">
                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">1</div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Instant Confirmation</h3>
                    <p className="text-muted-foreground">
                      Show a thank-you message immediately: "Thanks! We'll call within 2 hours." 
                      Send an auto-text or email so they know the form worked.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">2</div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Speed to Lead: Call Within 5 Minutes</h3>
                    <p className="text-muted-foreground">
                      Leads contacted within 5 minutes are 21x more likely to convert than those contacted after 30 minutes. 
                      Set up instant notifications to your phone.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">3</div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Auto-Text If You Can't Call</h3>
                    <p className="text-muted-foreground">
                      If you're on a job, send an automated text: "Hey [Name], got your request! I'm with a customer right now 
                      but I'll call you within the hour. —[Your Name]"
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold shrink-0">4</div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Follow Up if No Answer</h3>
                    <p className="text-muted-foreground">
                      If they don't pick up, leave a voicemail AND send a text. Follow up again the next day. 
                      Most lawn care companies give up after one attempt—don't be most companies.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Good vs Bad Examples */}
        <section className="py-16 bg-background">
          <div className="container px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                Good vs. Bad Form Examples
              </h2>

              <div className="space-y-8">
                {/* Bad Example 1 */}
                <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
                  <div className="bg-destructive/10 px-6 py-3 border-b flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-destructive" />
                    <span className="font-semibold text-destructive">Bad Example: Too Many Fields</span>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                      <div className="bg-muted/50 p-2 rounded">First Name</div>
                      <div className="bg-muted/50 p-2 rounded">Last Name</div>
                      <div className="bg-muted/50 p-2 rounded">Email *</div>
                      <div className="bg-muted/50 p-2 rounded">Phone</div>
                      <div className="bg-muted/50 p-2 rounded col-span-2">Street Address *</div>
                      <div className="bg-muted/50 p-2 rounded">City *</div>
                      <div className="bg-muted/50 p-2 rounded">State *</div>
                      <div className="bg-muted/50 p-2 rounded">Zip *</div>
                      <div className="bg-muted/50 p-2 rounded">Lot Size *</div>
                      <div className="bg-muted/50 p-2 rounded col-span-2">Describe your lawn needs in detail *</div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      <strong>Problem:</strong> 11 fields is overwhelming. Requiring email AND full address before you've even talked 
                      feels invasive. Most visitors will bounce.
                    </p>
                  </div>
                </div>

                {/* Good Example 1 */}
                <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
                  <div className="bg-primary/10 px-6 py-3 border-b flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    <span className="font-semibold text-primary">Good Example: Clean & Fast</span>
                  </div>
                  <div className="p-6">
                    <div className="grid gap-3 mb-4 text-sm max-w-sm">
                      <div className="bg-muted/50 p-2 rounded">Name</div>
                      <div className="bg-muted/50 p-2 rounded">Phone *</div>
                      <div className="bg-muted/50 p-2 rounded">Zip Code</div>
                      <div className="bg-muted/50 p-2 rounded">Service Needed ▼</div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      <strong>Why it works:</strong> 4 fields, takes 30 seconds, phone is required so you can call back. 
                      Service dropdown prepares you for the conversation.
                    </p>
                  </div>
                </div>

                {/* Bad Example 2 */}
                <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
                  <div className="bg-destructive/10 px-6 py-3 border-b flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-destructive" />
                    <span className="font-semibold text-destructive">Bad Example: Hidden Form</span>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-muted-foreground mb-2">
                      Form buried at the bottom of a "Contact" page that requires 3 clicks to find. 
                      No form on homepage or service pages.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <strong>Problem:</strong> If visitors have to hunt for your form, they'll call the competitor 
                      whose number was right on their homepage.
                    </p>
                  </div>
                </div>

                {/* Good Example 2 */}
                <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
                  <div className="bg-primary/10 px-6 py-3 border-b flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    <span className="font-semibold text-primary">Good Example: Multiple Touchpoints</span>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-muted-foreground mb-2">
                      Form in the hero, at the bottom of each service page, in the footer, 
                      plus a sticky mobile "Get Quote" button.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      <strong>Why it works:</strong> No matter where someone is on your site, 
                      they're never more than a scroll away from requesting a quote.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Checklist */}
        <section className="py-16 bg-muted/30">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                Lawn Care Quote Form Checklist
              </h2>

              <div className="bg-card p-8 rounded-xl border shadow-sm">
                <ul className="space-y-4">
                  {[
                    "Form has 4 fields or fewer (Name, Phone, Zip, Service)",
                    "Phone number is required—not optional",
                    "Form is visible above the fold on homepage",
                    "Form appears at bottom of every service page",
                    "Mobile site has sticky call/quote button",
                    "Honeypot field blocks bots (no CAPTCHA)",
                    "Thank-you message shows expected response time",
                    "Auto-text or email confirms receipt",
                    "You get instant notification on submissions",
                    "Follow-up call happens within 5 minutes",
                    "Voicemail + text sent if no answer",
                    "Second follow-up scheduled for next day"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="mt-8 text-center text-muted-foreground">
                Want a <Link to="/lawn-care-website-design/" className="text-primary hover:underline">professionally designed lawn care website</Link> with 
                optimized forms built in? We handle all of this for you.
              </p>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-16 bg-background">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center">
                Frequently Asked Questions
              </h2>

              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-card p-6 rounded-xl border shadow-sm">
                    <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Reading */}
        <section className="py-16 bg-muted/30">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl font-bold mb-6">Related Reading</h2>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/lawn-care-website-cost/">
                  <Button variant="outline">Lawn Care Website Cost Guide</Button>
                </Link>
                <Link to="/lawn-care-website-design/">
                  <Button variant="outline">Lawn Care Website Design</Button>
                </Link>
                <Link to="/service-pages-that-rank/">
                  <Button variant="outline">Service Pages That Rank</Button>
                </Link>
                <Link to="/growth-add-ons/gbp/">
                  <Button variant="outline">Google Business Profile</Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">
                Ready for a Website That Converts?
              </h2>
              <p className="text-lg opacity-90 mb-8">
                Our <Link to="/websites/" className="underline hover:opacity-80">website subscription</Link> includes 
                optimized quote forms, instant lead notifications, and everything else you need to turn visitors into customers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="#get-started">
                  <Button size="lg" variant="secondary">
                    Get Started
                  </Button>
                </a>
                <Link to="/websites/">
                  <Button size="lg" variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                    See Website Pricing
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Calendly */}
        <CalendlyEmbed />

        {/* Resources Link */}
        <section className="py-12 bg-background">
          <div className="container px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-muted-foreground">
                Looking for more marketing tips? Check out our <Link to="/resources/" className="text-primary hover:underline">free resources</Link> or 
                <Link to="/contact/" className="text-primary hover:underline"> get in touch</Link> with any questions.
              </p>
              <p className="text-sm text-muted-foreground mt-6">
                Last updated: {today}
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LawnCareQuoteForm;